import { AppVersionItem, DefaultStyles } from './version'
import type { ResourceItem } from './instance'

export type AppInstanceInfo = {
  id: string
  name: string
  newInstanceId: string
  uri: string
  version: string | undefined
  versionStatus: string | undefined
  daysToExpire: string
  openAsReadOnly: boolean
  readonly: boolean
  readOnlyTitle: string
  readOnlyReason: string
  engineURI: string
  engineUID: string
  engineParams: string
  isPublic: boolean
  canWriteInDir: boolean
  defaultInterfaceId: string
  currentVenv: string
  canInstallLibs: boolean
  hasActiveProcess: boolean
  needNewWebSocket: boolean
  hostName: string
  recoveredExistingInstance: boolean
  applicationLanguage: string
  quickMenu?: string
  quickMenuInterfaceId: string | null
  pythonVersion: string
  cpuArchitecture: string
  canEdit: boolean
  hasEditPermission: boolean
  resources?: string
  appHasDocumentation?: boolean
  defaultStyles?: DefaultStyles
  assetsStatus?: 'ok' | 'recovered' | 'broken'
  assetsVersionPath?: string
}

// Returned by POST /apps/open/ when opening requires creating a new engine:
// the request is accepted and handed off to a background task instead of
// blocking until the engine is ready (see plannings/open_app_async.md).
// The full AppInstanceInfo arrives later over the socket as an
// OPEN_APP_TASK_RESULT message.
export type OpenAppAsyncAccepted = {
  instanceId: string
  status: 'opening'
}

export type OpenAppResponse = AppInstanceInfo | OpenAppAsyncAccepted

export const isOpenAppAsyncAccepted = (res: OpenAppResponse): res is OpenAppAsyncAccepted =>
  (res as OpenAppAsyncAccepted).status === 'opening'

// Response of GET /apps/openStatus/ — the polling fallback for when the
// OPEN_APP_TASK_RESULT websocket message is missed (e.g. the socket dropped
// and reconnected mid-open). 'unknown' means there's no record for that
// (instance, folder, version) — never started, or the result already expired.
export type OpenAppStatus =
  | { status: 'opening' }
  | { status: 'unknown' }
  | { status: 'done'; success: true; appInfo: AppInstanceInfo }
  | { status: 'done'; success: false; error: string; logDetails?: string }

export type DepartmentDefaultInterface = {
  departmentsCodes: string[]
  interfaceId: string
}

export type CustomSeriesColorsValue = {
  id: number
  text: string
  color: string
}

export type CustomSeriesColorsProperties = {
  id: string
  title: string
  values: CustomSeriesColorsValue[]
}

export type CustomDiscreteSequenceColorPaletteValues = {
  id: number
  color: string
}

export type CustomDiscreteSequenceColorPaletteProperties = {
  id: string
  title: string
  values: CustomDiscreteSequenceColorPaletteValues[]
}

export enum DefaultTabsOnOpen {
  open_existing_version = 'open_existing_version',
  create_new_version = 'create_new_version',
  manage_versions = 'manage_versions'
}

export type AppProperties = {
  id?: string
  defaultVersion?: string
  installLibrariesOnOpen?: boolean
  openDefaultVersionOnOpen?: boolean
  defaultTabOnOpen?: DefaultTabsOnOpen
  applicationLanguage?: string
  pythonVersion?: PythonVersion
  cpuArchitecture?: CpuArchitecture | null
  engineResources?: string | null
  recommendedResources?: ResourceItem[] | null
  instanceTimeoutInSeconds?: number | null
  openAsReadOnlyByDefault?: boolean
}

export enum ApplicationOptions {
  Rename = 2,
  CopyAppToWorkspace = 3,
  Delete = 4,
  Share = 5,
  CreateAppFromThis = 6,
  DownloadApp = 7,
  CopyAppToTeam = 8,
  CopyAppToPublic = 9,
  SetThumbnail = 10,
  RemoveThumbnail = 11,
  CopyFolder = 14,
  CutFolder = 15,
  CopyAppSubMenu = 16,
  DirectAccessLink = 17,
  OpenWithOptions = 18,
  Open = 19
}

export enum ApplicationOptionsGroup {
  Recently = 'recently',
  MyApps = 'my_apps',
  Public = 'public'
}

export interface ExampleItem {
  code: string
  title: string
  description: string
  version: string
  logo?: string
  screenshots?: string[]
}

export interface AppVersionsDialogState {
  show?: boolean
  folder?: string
  defaultVersion?: AppVersionItem
  appProperties?: AppProperties
  openAsReadOnly?: boolean
  openOnNewInstance?: boolean
  canWriteInDir?: boolean
  resources?: string
  interfaceId?: string
  openWithOptions?: boolean
}

export interface ShowVersionsOnOpenData {
  show: boolean
  appProperties: AppProperties
  defaultVersion: AppVersionItem
  canWriteInDir: boolean
  hasOpenInstance: boolean
}

export type AppOptions = {
  label: string
  uri: string
  group: string
}

export enum PythonVersion {
  python3_9 = '3.9',
  python3_12 = '3.12'
}

export enum CpuArchitecture {
  x86 = 'x86',
  arm = 'arm'
}
export interface NodeEvaluatedOnStart {
  identifier: string
  title: string
}

export interface AppAdditionalInfo {
  nodes_evaluated_when_app_is_opened: NodeEvaluatedOnStart[]
}

export interface DirectAccessMetadata {
  companyCode: string
  companyName: string
  hasSamlConfig: boolean
  targetName: string
  version?: string | null
  openAsReadOnly: boolean
  requiresAuthentication: boolean
}

export interface DirectAccessResolution extends DirectAccessMetadata {
  folder: string
}

export interface ApplicationCardComponentProps {
  index: number
  item: any
  tabName?: string
  rename: boolean
  appToEditOrDelete: { path: string; tab: string }
  newName: string
  newNameError: { isRequired: boolean; notMatch: boolean }
  newNameIsTouched: boolean
  handleClickModelCard: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, uri: string) => void
  handleOpenOptions: (
    e: React.MouseEvent<any, MouseEvent>,
    uri: string,
    name: string,
    thumbnail?: string,
    tabName?: string
  ) => void
  handleCancel: () => void
  handleRename: () => void
  setNewName: (name: string) => void
  setNewNameIsTouched: (touched: boolean) => void
  selectedAppsOrFolders: string[]
  setSelectedAppsOrFolders: React.Dispatch<React.SetStateAction<string[]>>
}

export enum CopyOrMoveAction {
  Copy = 'copy',
  Move = 'move'
}

export interface ApplicationCopyOrCut {
  folders: string[]
  action: CopyOrMoveAction
}

export interface FolderAppCardComponentProps {
  index: number
  item: any
  rename: boolean
  appToEditOrDelete: { path: string; tab: string }
  newName: string
  newNameError: { isRequired: boolean; notMatch: boolean }
  newNameIsTouched: boolean
  handleClickFolderCard: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, uri: string) => void
  handleOpenOptions: (
    e: React.MouseEvent<any, MouseEvent>,
    uri: string,
    name: string,
    thumbnail?: string,
    tabName?: string,
    fromFolder?: boolean
  ) => void
  handleCancel: () => void
  handleRename: () => void
  setNewName: (name: string) => void
  setNewNameIsTouched: (touched: boolean) => void
  tabName?: string
  setSelectedAppsOrFolders: React.Dispatch<React.SetStateAction<string[]>>
  selectedAppsOrFolders: string[]
}
